import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

export const WAF_CAPTCHA_API_KEY = new InjectionToken<string>('WAF_CAPTCHA_API_KEY');

// Backstop for when renderCaptcha() loads but never invokes ANY callback
// (silent SDK failure). It must stay above AWS's own puzzle lifetime so it
// never pre-empts onPuzzleTimeout and kill a slow-but-legitimate solve — the
// user-facing escape is the Dismiss button, not this timer. Re-armed on each
// render attempt and cleared once an attempt produces a terminal callback, so
// it only measures "this render attempt went silent."
const CAPTCHA_TIMEOUT_MS = 300_000;

// Persistent explanatory header. The modal can appear unprompted mid-session,
// and renderCaptcha() is called with skipTitle: true (no AWS-provided heading),
// so without this the user sees only a bare puzzle. Rendered as a sibling of the
// widget child so it survives the SDK rendering into that child.
const HEADER_HTML = [
    '<div style="text-align: center; font-family: Roboto Condensed, sans-serif; margin-bottom: 16px;">',
    '  <div style="color: #0263b3; font-size: 16px; font-weight: 600; margin-bottom: 6px;">Verify you&rsquo;re human</div>',
    '  <div style="color: #666; font-size: 13px; line-height: 1.4;">YCharts runs a quick security check to protect against automated traffic. Complete the check below to continue.</div>',
    '</div>',
].join('');

const DISMISS_BUTTON_HTML = [
    '<div style="margin-top: 12px; text-align: center;">',
    '  <button class="waf-captcha-dismiss" style="',
    '    background: none; border: none; color: #666; font-size: 13px;',
    "    font-family: 'Roboto Condensed', sans-serif;",
    '    cursor: pointer; text-decoration: underline;',
    // Ensure at least a 44px touch target for mobile (WCAG 2.5.5 / Apple HIG).
    '    display: inline-flex; align-items: center; justify-content: center;',
    '    min-height: 44px; padding: 0 16px;',
    '  ">Dismiss</button>',
    '</div>',
].join('');

@Injectable()
export class WafCaptchaService {
    private pendingPromise: Promise<void> | null = null;

    private resolvePromise: (() => void) | null = null;

    private rejectPromise: ((reason: Error) => void) | null = null;

    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    private overlayElement: HTMLElement | null = null;

    private captchaContainer: HTMLElement | null = null;

    private focusTrap: ConfigurableFocusTrap | null = null;

    private previousActiveElement: Element | null = null;

    private previousOverflow: string = '';

    private settled = false;

    private sdkMissingLogged = false;

    private renderGeneration = 0;

    constructor(
        private focusTrapFactory: ConfigurableFocusTrapFactory,
        @Optional() @Inject(WAF_CAPTCHA_API_KEY) private apiKey: string | null
    ) {}

    solveCaptcha(): Promise<void> {
        if (this.pendingPromise) {
            return this.pendingPromise;
        }

        if (typeof AwsWafCaptcha === 'undefined') {
            if (!this.sdkMissingLogged) {
                // eslint-disable-next-line no-console
                console.warn('WAF SDK: AwsWafCaptcha is not available — jsapi.js may not be loaded');
                this.sdkMissingLogged = true;
            }
            return Promise.reject(new Error('AwsWafCaptcha not available'));
        }

        if (!this.apiKey) {
            return Promise.reject(new Error('WAF_CAPTCHA_API_KEY not configured'));
        }

        this.settled = false;

        this.pendingPromise = new Promise<void>((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;

            this.createOverlay();
            this.renderPuzzle();
        }).finally(() => {
            this.cleanup();
        });

        return this.pendingPromise;
    }

    private renderPuzzle(): void {
        if (!this.captchaContainer || this.settled) return;

        this.renderGeneration += 1;
        const generation = this.renderGeneration;

        this.resetTimeout();

        // renderCaptcha() owns and replaces the element it is given, so hand it
        // a dedicated child. The Dismiss control lives as a sibling and survives
        // SDK rendering, preserving the user's escape hatch even if the widget
        // renders slowly or never fires a callback. The "Loading…" message lives
        // inside the widget child so the SDK's own render replaces it.
        this.captchaContainer.innerHTML = '';
        this.captchaContainer.insertAdjacentHTML('beforeend', HEADER_HTML);
        const widgetEl = document.createElement('div');
        widgetEl.style.cssText = 'width: 100%;';
        widgetEl.innerHTML =
            '<div style="text-align: center; padding: 20px; color: #666; font-family: Roboto Condensed, sans-serif;">Loading security check…</div>';
        this.captchaContainer.appendChild(widgetEl);
        this.captchaContainer.insertAdjacentHTML('beforeend', DISMISS_BUTTON_HTML);
        this.wireDismissButton();

        try {
            AwsWafCaptcha!.renderCaptcha(widgetEl, {
                apiKey: this.apiKey!,
                onSuccess: () => {
                    if (generation !== this.renderGeneration) return;
                    this.settle(true);
                },
                onError: () => {
                    if (generation !== this.renderGeneration) return;
                    this.showRetryState();
                },
                onPuzzleTimeout: () => {
                    if (generation !== this.renderGeneration) return;
                    this.showRetryState();
                },
                dynamicWidth: true,
                skipTitle: true,
            });
        } catch (error) {
            if (generation !== this.renderGeneration) return;
            // eslint-disable-next-line no-console
            console.warn('WAF SDK: renderCaptcha threw', error);
            this.showRetryState();
        }
    }

    private showRetryState(): void {
        if (this.settled || !this.captchaContainer) return;

        // Supersede this render attempt so any late callback from it (e.g. a
        // delayed onSuccess fired after onError) is ignored by the generation
        // guards; the retry re-renders under a fresh generation.
        this.renderGeneration += 1;

        // This render attempt produced a terminal callback (or threw), so the
        // silent-failure safety net no longer applies; it re-arms on retry.
        this.clearSafetyNet();

        this.captchaContainer.innerHTML = [
            '<div style="text-align: center; font-family: Roboto Condensed, sans-serif; padding: 20px;">',
            '  <div style="color: #333; font-size: 15px; margin-bottom: 16px;">Security check failed. Please try again.</div>',
            '  <button class="waf-captcha-retry" style="',
            '    background: #0263b3; color: white; border: none; border-radius: 4px;',
            "    font-family: 'Roboto Condensed', sans-serif;",
            '    font-size: 14px; cursor: pointer;',
            // 44px min touch target for mobile (WCAG 2.5.5 / Apple HIG).
            '    display: inline-flex; align-items: center; justify-content: center;',
            '    min-height: 44px; padding: 0 24px;',
            '  ">Try again</button>',
            DISMISS_BUTTON_HTML,
            '</div>',
        ].join('');

        const retryBtn = this.captchaContainer.querySelector('.waf-captcha-retry');
        if (retryBtn) {
            retryBtn.addEventListener('click', () => this.renderPuzzle());
        }

        this.wireDismissButton();
    }

    private wireDismissButton(): void {
        const dismissBtn = this.captchaContainer?.querySelector('.waf-captcha-dismiss');
        if (dismissBtn) {
            dismissBtn.addEventListener('click', () => this.settle(false, new Error('CAPTCHA dismissed by user')));
        }
    }

    private settle(success: boolean, error?: Error): void {
        if (this.settled) return;
        this.settled = true;

        if (success) {
            if (this.resolvePromise) this.resolvePromise();
        } else if (this.rejectPromise) this.rejectPromise(error ?? new Error('CAPTCHA failed'));
    }

    private createOverlay(): void {
        this.previousActiveElement = document.activeElement;
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        this.overlayElement = document.createElement('div');
        this.overlayElement.setAttribute('role', 'dialog');
        this.overlayElement.setAttribute('aria-modal', 'true');
        this.overlayElement.setAttribute('aria-label', 'Security verification');
        this.overlayElement.setAttribute('tabindex', '-1');
        this.overlayElement.style.cssText = [
            'position: fixed',
            'top: 0',
            'left: 0',
            'width: 100%',
            'height: 100%',
            'background: rgba(0, 0, 0, 0.6)',
            'display: flex',
            // Scroll (not clip) when the puzzle is taller than the viewport, e.g.
            // on small phones or landscape. The card uses margin:auto rather than
            // align-items:center so an overflowing card stays reachable from the
            // top and the overlay scrolls, instead of clipping the header/buttons.
            'overflow-y: auto',
            'padding: 16px',
            'box-sizing: border-box',
            'z-index: 99999',
        ].join('; ');

        this.captchaContainer = document.createElement('div');
        this.captchaContainer.className = 'waf-captcha-container';
        this.captchaContainer.style.cssText =
            'background: white; border-radius: 8px; padding: 24px; max-width: 400px; width: 100%; box-sizing: border-box; margin: auto;';
        this.overlayElement.appendChild(this.captchaContainer);

        document.body.appendChild(this.overlayElement);

        this.focusTrap = this.focusTrapFactory.create(this.overlayElement);
        this.focusTrap.focusInitialElementWhenReady();
    }

    private clearSafetyNet(): void {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    private resetTimeout(): void {
        this.clearSafetyNet();
        this.timeoutId = setTimeout(() => {
            this.settle(false, new Error('CAPTCHA safety-net timed out — renderCaptcha may have failed'));
        }, CAPTCHA_TIMEOUT_MS);
    }

    private cleanup(): void {
        this.clearSafetyNet();

        if (this.focusTrap) {
            this.focusTrap.destroy();
            this.focusTrap = null;
        }

        if (this.overlayElement) {
            this.overlayElement.remove();
            this.overlayElement = null;
        }

        this.captchaContainer = null;
        document.body.style.overflow = this.previousOverflow;

        if (this.previousActiveElement instanceof HTMLElement) {
            this.previousActiveElement.focus();
        }
        this.previousActiveElement = null;

        this.pendingPromise = null;
        this.resolvePromise = null;
        this.rejectPromise = null;
    }
}
